import React from 'react'
import {BlockComponentProps} from '../index'
import {useSection} from '../../Section'
import {PurchaseStep, usePurchaseForm} from './PurchaseFormProvider'
import styles from './PurchaseForm.module.css'
import {usePurchase} from '../../PurchaseContext'
import BackButtonButton from './BackButton'
import NextStepButton from './NextStepButton'
import PurchaseButton from './PurchaseButton'
import {PurchaseFormProps} from '.'

interface PurchaseFormActionsProps {
  ButtonComponent: React.FC<any>
  config: PurchaseFormProps & BlockComponentProps
}

export default function PurchaseFormActions(props: PurchaseFormActionsProps) {
  const {step, setStep, useSameAddressForShipping} = usePurchaseForm()
  const {selectedTicket, isProcessing} = usePurchase()
  const {calculateVisibility} = useSection()

  const isFreeTicket = selectedTicket ? selectedTicket.price === 0 : false
  const billingStepEnabled = selectedTicket
    ? selectedTicket.billing_address_enabled ?? false
    : false
  const shippingStepEnabled = selectedTicket
    ? selectedTicket.billing_address_enabled ?? false
    : false

  const {ButtonComponent, config} = props

  const nextStep = getNextStep(
    step,
    isFreeTicket,
    billingStepEnabled,
    shippingStepEnabled,
    useSameAddressForShipping,
  )

  const prevStep = getPrevStep(
    step,
    billingStepEnabled,
    shippingStepEnabled,
    useSameAddressForShipping,
  )
  const disabled = isProcessing

  const onBack = () => {
    if (!prevStep) {
      return
    }

    setStep(prevStep)
    calculateVisibility()
  }

  const handleContinue = () => {
    if (!nextStep) {
      return
    }

    setStep(nextStep)
    calculateVisibility()
  }

  return (
    <div
      className={styles.formButtonBox}
      style={{
        gap: '15px',
        marginTop: config.input.spacing,
        justifyContent: config.buttonsAlignment,
        alignItems: 'stretch',
      }}
    >
      {prevStep && <BackButtonButton {...config} onClick={onBack} />}
      {nextStep && (
        <div
          className={styles.purchaseFormButtonHolder}
          style={{width: config.buttonStyle.width + '%'}}
        >
          <NextStepButton
            ButtonComponent={ButtonComponent}
            {...config}
            disabled={disabled}
            onClick={handleContinue}
          />
        </div>
      )}

      {!nextStep && (
        <div
          className={styles.purchaseFormButtonHolder}
          style={{width: config.buttonStyle.width + '%'}}
        >
          <PurchaseButton
            ButtonComponent={ButtonComponent}
            {...config}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  )
}

function getNextStep(
  step: PurchaseStep,
  isFreeTicket: boolean,
  billingStepEnabled: boolean,
  shippingStepEnabled: boolean,
  useSameAddressForShipping: boolean,
): PurchaseStep | null {
  const needsShippingFields = shippingStepEnabled && !useSameAddressForShipping

  if (step === 'customer_details' && billingStepEnabled) {
    return 'address_billing'
  }

  if (step === 'customer_details' && !isFreeTicket && !billingStepEnabled) {
    return 'order_confirmation'
  }

  if (step === 'customer_details' && isFreeTicket && !billingStepEnabled) {
    return null
  }

  if (step === 'address_billing' && !isFreeTicket && needsShippingFields) {
    return 'address_shipping'
  }

  if (step === 'address_billing' && !isFreeTicket && !needsShippingFields) {
    return 'order_confirmation'
  }

  if (step === 'address_billing' && isFreeTicket && needsShippingFields) {
    return 'address_shipping'
  }

  if (step === 'address_billing' && isFreeTicket && !needsShippingFields) {
    return null
  }

  if (step === 'address_shipping' && !isFreeTicket) {
    return 'order_confirmation'
  }

  if (step === 'address_shipping' && isFreeTicket) {
    return null
  }

  if (step === 'order_confirmation') {
    return null
  }

  throw new Error('Unhandled next step')
}

function getPrevStep(
  step: PurchaseStep,
  billingStepEnabled: boolean,
  shippingStepEnabled: boolean,
  useSameAddressForShipping: boolean,
): PurchaseStep | null {
  const needsShippingFields = shippingStepEnabled && !useSameAddressForShipping

  if (step === 'address_billing') {
    return 'customer_details'
  }

  if (step === 'address_shipping' && billingStepEnabled) {
    return 'address_billing'
  }

  if (step === 'address_shipping' && !billingStepEnabled) {
    return 'customer_details'
  }

  if (step === 'order_confirmation' && needsShippingFields) {
    return 'address_shipping'
  }

  if (step === 'order_confirmation' && billingStepEnabled) {
    return 'address_billing'
  }

  if (step === 'order_confirmation' && shippingStepEnabled) {
    return 'address_shipping'
  }

  if (step === 'order_confirmation') {
    return 'customer_details'
  }

  return null
}
